// src/pages/user/OwnerQuoteEditor.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FileText, RefreshCw, CheckCircle, Clock, XCircle, Send,
  ThumbsUp, ThumbsDown, Eye, Download, FileSpreadsheet,
  Calendar, User, DollarSign, Package, PieChart, AlertCircle,
  Percent as PercentIcon, Edit3, ArrowLeft, Building,
} from "lucide-react";
import { getUserType, USER_TYPES } from "../../config/roles";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/ToastProvider";
import { injectUserStyles, statusBadgeClass, fmtStatus } from "./userStyles";

const BASE    = process.env.REACT_APP_BACKEND_URL || "";
const APP_URL = process.env.REACT_APP_APP_URL     || "";

const STYLE_ID = "oqe-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .oqe-layout { display:grid; grid-template-columns:1fr 300px; gap:20px; align-items:start; }
    @media (max-width:1199px) { .oqe-layout { grid-template-columns:1fr; } }
    .oqe-table { width:100%; border-collapse:collapse; font-size:13px; }
    .oqe-table thead tr { background:#0c1118; }
    .oqe-table th { padding:12px 14px; text-align:left; font-family:'DM Sans',sans-serif; font-size:9.5px; letter-spacing:.04em; color:rgba(255,255,255,.5); white-space:nowrap; }
    .oqe-table td { padding:12px 14px; border-bottom:1px solid #f3f4f6; vertical-align:middle; }
    .oqe-table tbody tr:last-child td { border-bottom:none; }
    .oqe-table tbody tr:hover td { background:#fafafa; }
    .oqe-table tbody tr.editing td { background:rgba(249,171,20,.05); }
    .oqe-tfoot-sub td { padding:12px 14px; background:#f9fafb; border-top:2px solid #e8ecf0; }
    .oqe-tfoot-total td { padding:14px; background:#0c1118; border-top:2px solid #f9ab14; }
    .oqe-cat-pill { display:inline-flex; padding:2px 8px; border-radius:20px; font-size:10px; font-family:'DM Sans',sans-serif; letter-spacing:.05em; background:#f3f4f6; color:#374151; text-transform:capitalize; margin-top:4px; }
    .oqe-price-input {
      width:130px; padding:7px 10px 7px 28px;
      border:1px solid #e2e8f0; border-radius:6px; font-size:13px;
      outline:none; transition:border-color 150ms; text-align:right;
      font-family:'DM Sans',sans-serif;
    }
    .oqe-price-input:focus { border-color:#f9ab14; box-shadow:0 0 0 3px rgba(249,171,20,.1); }
    .oqe-price-input.editing { border-color:#f9ab14; background:#fffbeb; }
    .oqe-price-wrap { position:relative; display:inline-flex; }
    .oqe-price-icon { position:absolute; left:9px; top:50%; transform:translateY(-50%); color:#9ca3af; pointer-events:none; }
    .oqe-right-panel { display:flex; flex-direction:column; gap:20px; }
    .oqe-donut-legend { display:flex; flex-direction:column; gap:6px; }
    .oqe-legend-row { display:flex; align-items:center; justify-content:space-between; padding:8px 10px; background:#f9fafb; border-radius:6px; }
    .oqe-legend-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
    .oqe-meta-row { padding:12px 0; border-bottom:1px solid #f3f4f6; }
    .oqe-meta-row:last-child { border-bottom:none; }
    .oqe-meta-label { font-family:'DM Sans',sans-serif; font-size:9.5px; letter-spacing:.04em; color:#9ca3af; margin-bottom:5px; }
    .oqe-meta-val   { font-size:13.5px; font-weight:500; color:#0c1118; }
    .oqe-breadcrumb { display:flex; align-items:center; gap:6px; font-size:12px; color:#9ca3af; margin-bottom:16px; }
    .oqe-breadcrumb a { color:#9ca3af; text-decoration:none; transition:color 150ms; }
    .oqe-breadcrumb a:hover { color:#f9ab14; }
    .oqe-breadcrumb-sep { color:#d1d5db; }
  `;
  document.head.appendChild(el);
}

/* ── Helpers ── */
async function jfetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, { headers: { "Content-Type": "application/json" }, credentials: "include", ...opts });
  if (!res.ok) { const text = await res.text().catch(() => ""); const err = new Error(`${res.status} ${res.statusText}`); err.status = res.status; err.body = text; throw err; }
  return res.json();
}

const money = x => Number(x || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/* ── Donut (preserved exactly) ── */
function Donut({ parts }) {
  const total = parts.reduce((s, p) => s + Number(p.value || 0), 0) || 0;
  let acc = 0;
  const R = 36, C = 2 * Math.PI * R;
  return (
    <svg viewBox="0 0 80 80" className="w-40 h-40">
      <circle r={R} cx="40" cy="40" fill="transparent" stroke="#e5e7eb" strokeWidth="8" />
      {parts.map((p, i) => {
        const dash = (p.value / total) * C, gap = C - dash;
        const seg = <circle key={i} r={R} cx="40" cy="40" fill="transparent" strokeWidth="8" strokeDasharray={`${dash} ${gap}`} strokeDashoffset={-acc} style={{ stroke: `hsl(${(i*57)%360} 70% 50%)` }} />;
        acc += dash; return seg;
      })}
      <circle r={R - 14} cx="40" cy="40" fill="white" />
      <text x="40" y="35" textAnchor="middle" className="text-xs font-bold fill-slate-700">Total</text>
      <text x="40" y="48" textAnchor="middle" className="text-[10px] font-bold fill-slate-900">${money(total)}</text>
    </svg>
  );
}

/* ── Main ── */
export default function OwnerQuoteEditor() {
  useEffect(() => { injectLocal(); }, []);

  const { quoteId }    = useParams();
  const navigate       = useNavigate();
  const { user }       = useAuth();
  const userType       = getUserType(user);

  const [projectId,    setProjectId]    = useState(null);
  const [quoteMeta,    setQuoteMeta]    = useState(null);
  const [takeoff,      setTakeoff]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(null);
  const [editingRow,   setEditingRow]   = useState(null);
  const [sending,      setSending]      = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [wastePct,     setWastePct]     = useState(0);
  const [markupPct,    setMarkupPct]    = useState(0);
  const toast = useToast();

  const resolveIds = useCallback(async () => {
    try {
      const q = await jfetch(`/api/quotes/by-project?ids=${quoteId}`);
      console.log(q);
      if (q.map[quoteId]?.projectId) {
        setProjectId(q.map[quoteId]?.projectId);
        setQuoteMeta({ id: q.map[quoteId].id, status: q.map[quoteId].status, createdAt: q.map[quoteId].createdAt, updatedAt: q.map[quoteId].updatedAt, requester: q.map[quoteId].requester || null, project: q.map[quoteId].project || null });
        return q.map[quoteId].projectId;
      }
    } catch {}
    setProjectId(quoteId); setQuoteMeta(null); return quoteId;
  }, [quoteId]);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const pid = await resolveIds();
      const r = await jfetch(`/api/estimate/takeoff?projectId=${encodeURIComponent(pid)}`);
      setTakeoff({ project: r.project, items: Array.isArray(r.items) ? r.items.map(x => ({ ...x })) : [], subtotal: Number(r.subtotal || 0), total: Number(r.total || 0), stats: r.stats || { byCategory: [] } });
      setWastePct(0); setMarkupPct(0);
    } catch (err) {
      setLoadError(err.status ? `${err.status} ${err.message}` : err.message);
      setTakeoff(null);
    } finally { setLoading(false); setEditingRow(null); }
  }, [resolveIds]);

  useEffect(() => { if (quoteId) load(); }, [quoteId, load]);

  const canEditItems = useMemo(() => {
    const ownerOrStaff = userType === USER_TYPES.OWNER || userType === USER_TYPES.STAFF;
    return !!(ownerOrStaff || user?.role === "admin");
  }, [userType, user]);

  const subtotal  = useMemo(() => (takeoff?.items || []).reduce((s, i) => s + Number(i.lineTotal || 0), 0), [takeoff]);
  const wasteVal  = useMemo(() => subtotal * Number(wastePct || 0), [subtotal, wastePct]);
  const withWaste = subtotal + wasteVal;
  const markupVal = useMemo(() => withWaste * Number(markupPct || 0), [withWaste, markupPct]);
  const finalTotal = withWaste + markupVal;

  const grouped = useMemo(() => {
    if (!takeoff?.items?.length) return [];
    const map = new Map();
    takeoff.items.forEach(it => { const v = Number(it.lineTotal || 0); map.set(it.category || "other", (map.get(it.category || "other") || 0) + v); });
    return Array.from(map, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 6);
  }, [takeoff]);

  function updateRow(idx, field, value) {
    setEditingRow(idx);
    setTakeoff(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      const it = { ...next.items[idx] };
      if (field === "unitPrice") { const v = Number(value || 0); it.unitPrice = v; it.lineTotal = v * Number(it.qty || 0); }
      next.items = [...next.items]; next.items[idx] = it; return next;
    });
  }

  const exportPdfUrl = projectId ? `${BASE}/api/estimate/takeoff/export.pdf?projectId=${encodeURIComponent(projectId)}` : "#";
  const exportCsvUrl = projectId ? `${BASE}/api/estimate/takeoff/export.csv?projectId=${encodeURIComponent(projectId)}` : "#";

  async function previewPdf() {
    if (!projectId) return;
    try {
      setPreviewLoading(true);
      const res = await fetch(exportPdfUrl, { method: "GET", credentials: "include" });
      if (!res.ok) throw new Error(`PDF failed: ${res.status}`);
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a"); a.href = url; a.download = `takeoff-${projectId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to generate PDF."); console.error(e); }
    finally { setPreviewLoading(false); }
  }

  async function sendToClient() {
    try {
      if (!quoteMeta?.id) { toast.warning?.("This action requires a quote context. Open the editor from a Quote row."); return; }
      setSending(true);
      const res = await fetch(`${BASE}/api/quotes/${quoteMeta.id}/send-to-client`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      if (!res.ok) { const txt = await res.text().catch(() => ""); throw new Error(`Failed to send: ${res.status} ${res.statusText}${txt ? " — " + txt : ""}`); }
      toast.success("Quote was sent to the client.");
      window.location.href = `${APP_URL || ""}/dashboard/org-quotes#${quoteMeta.id}`;
    } catch (err) { console.error(err); toast.error(err.message || "Failed to send the quote to the client."); }
    finally { setSending(false); }
  }

  /* ── Loading ── */
  if (loading) return (
    <div className="up-loading" style={{ minHeight:"60vh" }}>
      <div className="up-spinner" style={{ width:28, height:28 }} />
      <span>Loading quote editor…</span>
    </div>
  );

  /* ── Error ── */
  if (loadError || !takeoff) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh", padding:24 }}>
      <div className="up-panel" style={{ maxWidth:420, width:"100%", textAlign:"center", padding:"40px 32px" }}>
        <div style={{ width:60, height:60, borderRadius:"50%", background:"rgba(239,68,68,.1)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
          <AlertCircle size={28} style={{ color:"#ef4444" }} />
        </div>
        <div style={{ fontSize:"16px", fontWeight:600, color:"#0c1118", marginBottom:8 }}>Unable to load</div>
        <div style={{ fontSize:"13px", color:"#9ca3af", marginBottom:24 }}>{loadError || "No data."}</div>
        <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
          <button className="up-btn up-btn-outline" onClick={() => navigate(-1)}>Go Back</button>
          <button className="up-btn up-btn-dark" onClick={load}>Retry</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily:"'DM Sans', sans-serif" }}>
      {/* Breadcrumb */}
      <div className="oqe-breadcrumb">
        <Link to="/dashboard/quotes-org">Quotes</Link>
        <span className="oqe-breadcrumb-sep">/</span>
        <span style={{ color:"#0c1118", fontWeight:500 }}>Quote Editor</span>
      </div>

      {/* Header */}
      <div style={{ display:"flex", flexWrap:"wrap", alignItems:"flex-start", justifyContent:"space-between", gap:16, marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"flex-start", gap:14, minWidth:0 }}>
          <div style={{ width:48, height:48, borderRadius:10, background:"#0c1118", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <FileText size={22} style={{ color:"#f9ab14" }} />
          </div>
          <div>
            <div className="up-title">{takeoff.project?.name || projectId}</div>
            <div style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:8, marginTop:4 }}>
              {quoteMeta?.status && <span className={statusBadgeClass(quoteMeta.status)}>{fmtStatus(quoteMeta.status)}</span>}
              {quoteMeta?.updatedAt && (
                <span style={{ fontSize:"12px", color:"#9ca3af", display:"flex", alignItems:"center", gap:4 }}>
                  <Calendar size={11} /> Updated {new Date(quoteMeta.updatedAt).toLocaleDateString()}
                </span>
              )}
              {canEditItems && (
                <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:"11.5px", fontFamily: "'DM Sans', sans-serif", letterSpacing:".06em", color:"#f9ab14" }}>
                  <Edit3 size={11} /> EDITABLE
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          <Link to="/dashboard/quotes-org" className="up-btn up-btn-outline"><ArrowLeft size={13} /> Back</Link>
          <button className="up-btn up-btn-outline" onClick={load}><RefreshCw size={13} /> Refresh</button>
          <button className="up-btn up-btn-dark" onClick={previewPdf} disabled={!projectId || previewLoading}>
            {previewLoading ? <><RefreshCw size={13} style={{ animation:"upSpin .7s linear infinite" }} /> Generating…</> : <><Download size={13} /> Preview PDF</>}
          </button>
          <button className="up-btn up-btn-amber" onClick={sendToClient} disabled={sending}>
            <Send size={13} /> {sending ? "Sending…" : "Send To Client"}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="oqe-layout">
        {/* Left: table + export */}
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
          <div className="up-panel">
            <div style={{ background:"#0c1118", padding:"16px 20px" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize:"1.3rem", letterSpacing:".06em", color:"#fff", display:"flex", alignItems:"center", gap:8 }}>
                    <Package size={18} /> Material Takeoff &amp; Pricing
                  </div>
                  <div style={{ fontSize:"11.5px", color:"rgba(255,255,255,.4)", marginTop:3, fontFamily: "'DM Sans', sans-serif", letterSpacing:".04em" }}>
                    {takeoff.items?.length || 0} items · Total: ${money(finalTotal)}
                  </div>
                </div>
              </div>
            </div>

            {!takeoff.items?.length ? (
              <div className="up-empty">
                <Package size={36} style={{ color:"#d1d5db", margin:"0 auto" }} />
                <div className="up-empty-title">No Items Yet</div>
                <div className="up-empty-sub">Open this project in the 3D editor to generate takeoff lines, then refresh.</div>
              </div>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table className="oqe-table">
                  <thead><tr>
                    <th>Component</th>
                    <th style={{ textAlign:"right" }}>Qty</th>
                    <th>Unit</th>
                    <th style={{ textAlign:"right" }}>Unit Price</th>
                    <th style={{ textAlign:"right" }}>Line Total</th>
                  </tr></thead>
                  <tbody>
                    {takeoff.items.map((it, idx) => (
                      <tr key={it.id || `${it.key}-${idx}`} className={editingRow === idx ? "editing" : ""}>
                        <td>
                          <div style={{ fontWeight:500, color:"#0c1118", fontSize:"13.5px" }}>{it.label || it.key}</div>
                          {it.category && <div className="oqe-cat-pill">{it.category}</div>}
                        </td>
                        <td style={{ textAlign:"right", fontWeight:600, fontFamily: "'DM Sans', sans-serif", fontSize:"12px" }}>{Number(it.qty).toLocaleString()}</td>
                        <td style={{ fontSize:"12.5px", color:"#6b7280" }}>{it.unit}</td>
                        <td style={{ textAlign:"right" }}>
                          {canEditItems ? (
                            <div className="oqe-price-wrap">
                              <DollarSign size={13} className="oqe-price-icon" />
                              <input className={`oqe-price-input ${editingRow === idx ? "editing" : ""}`}
                                value={String(it.unitPrice ?? "")} onChange={e => updateRow(idx, "unitPrice", e.target.value)} onFocus={() => setEditingRow(idx)} />
                            </div>
                          ) : (
                            <span style={{ fontWeight:600, fontSize:"13px", color:"#0c1118" }}>${money(it.unitPrice)}</span>
                          )}
                        </td>
                        <td style={{ textAlign:"right", fontWeight:700, fontSize:"13.5px", color:"#0c1118" }}>${money(it.lineTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="oqe-tfoot-sub">
                      <td colSpan={4} style={{ fontWeight:600, color:"#374151", fontSize:"13.5px" }}>Subtotal</td>
                      <td style={{ textAlign:"right", fontWeight:700, fontSize:"15px", color:"#0c1118" }}>${money(subtotal)}</td>
                    </tr>
                    <tr className="oqe-tfoot-total">
                      <td colSpan={4}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, fontFamily: "'DM Sans', sans-serif", fontSize:"1.3rem", letterSpacing:".04em", color:"#fff" }}>
                          <DollarSign size={18} /> Final Total
                        </div>
                      </td>
                      <td style={{ textAlign:"right" }}>
                        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize:"2rem", fontWeight:700, color:"#f9ab14", letterSpacing:".02em" }}>${money(finalTotal)}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Export */}
          <div className="up-panel">
            <div className="up-panel-head">
              <div className="up-panel-title"><Download size={15} style={{ color:"#f9ab14" }} /> Export Options</div>
            </div>
            <div style={{ padding:"16px 20px", display:"flex", gap:10, flexWrap:"wrap" }}>
              <a className={`up-btn up-btn-dark ${!projectId ? "disabled" : ""}`} href={exportPdfUrl} target="_blank" rel="noreferrer" style={{ textDecoration:"none", pointerEvents: projectId ? "auto" : "none", opacity: projectId ? 1 : .5 }}>
                <Download size={13} /> Export PDF
              </a>
              <a className={`up-btn up-btn-outline ${!projectId ? "disabled" : ""}`} href={exportCsvUrl} style={{ textDecoration:"none", pointerEvents: projectId ? "auto" : "none", opacity: projectId ? 1 : .5 }}>
                <FileSpreadsheet size={13} /> Export CSV
              </a>
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div className="oqe-right-panel">
          {/* Cost distribution */}
          <div className="up-panel">
            <div className="up-panel-head">
              <div className="up-panel-title"><PieChart size={15} style={{ color:"#f9ab14" }} /> Cost Distribution</div>
            </div>
            <div style={{ padding:"16px 20px" }}>
              {takeoff.items?.length ? (
                <>
                  <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}><Donut parts={grouped} /></div>
                  <div className="oqe-donut-legend">
                    {grouped.map((p, i) => (
                      <div className="oqe-legend-row" key={i}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div className="oqe-legend-dot" style={{ background:`hsl(${(i*57)%360} 70% 50%)` }} />
                          <span style={{ fontSize:"12.5px", color:"#374151", textTransform:"capitalize" }}>{p.label}</span>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ fontSize:"12.5px", fontWeight:700, color:"#0c1118", fontFamily: "'DM Sans', sans-serif" }}>${money(p.value)}</div>
                          <div style={{ fontSize:"10px", color:"#9ca3af" }}>{finalTotal ? ((p.value/finalTotal)*100).toFixed(1) : "0.0"}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="up-empty"><PieChart size={28} style={{ color:"#d1d5db", margin:"0 auto" }} /><div className="up-empty-sub">No data to visualize</div></div>
              )}
            </div>
          </div>

          {/* Project info */}
          <div className="up-panel">
            <div className="up-panel-head">
              <div className="up-panel-title"><Building size={15} style={{ color:"#f9ab14" }} /> Project Information</div>
            </div>
            <div style={{ padding:"14px 20px" }}>
              <div className="oqe-meta-row">
                <div className="oqe-meta-label">Project Name</div>
                <div className="oqe-meta-val">{takeoff.project?.name || projectId}</div>
              </div>
              {quoteMeta?.id && (<>
                <div className="oqe-meta-row">
                  <div className="oqe-meta-label">Quote ID</div>
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize:"11.5px", color:"#6b7280", wordBreak:"break-all" }}>{quoteMeta.id}</div>
                </div>
                <div className="oqe-meta-row">
                  <div className="oqe-meta-label">Status</div>
                  <span className={statusBadgeClass(quoteMeta.status)}>{fmtStatus(quoteMeta.status)}</span>
                </div>
                <div className="oqe-meta-row">
                  <div className="oqe-meta-label">Requested By</div>
                  <div className="oqe-meta-val">{quoteMeta.requester?.email || "—"}</div>
                </div>
                <div className="oqe-meta-row">
                  <div className="oqe-meta-label">Created</div>
                  <div className="oqe-meta-val" style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <Calendar size={12} style={{ color:"#9ca3af" }} />
                    {new Date(quoteMeta.createdAt).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" })}
                  </div>
                </div>
                <div className="oqe-meta-row">
                  <div className="oqe-meta-label">Last Updated</div>
                  <div className="oqe-meta-val" style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <Calendar size={12} style={{ color:"#9ca3af" }} />
                    {new Date(quoteMeta.updatedAt).toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" })}
                  </div>
                </div>
              </>)}
            </div>
          </div>

          {/* Quick actions */}
          <div className="up-panel">
            <div className="up-panel-head"><div className="up-panel-title">Quick Actions</div></div>
            <div style={{ padding:"12px 16px", display:"flex", flexDirection:"column", gap:8 }}>
              <button className="up-btn up-btn-dark" onClick={previewPdf} disabled={!projectId} style={{ justifyContent:"center" }}>
                <Download size={13} /> Preview PDF
              </button>
              <button className="up-btn up-btn-amber" onClick={sendToClient} style={{ justifyContent:"center" }}>
                <Send size={13} /> Send To Client
              </button>
              <Link to="/dashboard/quotes-org" className="up-btn up-btn-outline" style={{ textDecoration:"none", justifyContent:"center" }}>
                <ArrowLeft size={13} /> Back to All Quotes
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
